const regexPhone =
  /^(?:\+?\d{1,3}|0\d{1,3}|00\d{1,2})?(?:\s?\(\d+\))?(?:[-/\s.]|\d)+$/;

/**
 * Builds the backend filter query string from the sidebar filter controls.
 *
 * The first section (index 0) uses square bracket syntax `[...]` (main/unit filters).
 * Subsequent sections use curly bracket syntax `{...}` (lead, notes, etc.).
 * Fields within a section are joined with " & ".
 *
 * Returns an empty string when no filters are active.
 */
export function buildLeadsQueryString({
  mains,
  showOnlyMine,
}: {
  mains: MainSection[];
  showOnlyMine: boolean;
}): string {
  let query = '(';

  mains.forEach((main, i) => {
    const open = i === 0 ? '[' : '{';
    const close = i === 0 ? ']' : '}';

    let section = open;

    Object.keys(main).forEach((key) => {
      if (key === 'userUpdated' && showOnlyMine && i !== 0) {
        return;
      }

      const field = main[key];

      if (!isFieldControl(field)) {
        return;
      }

      const part = field.generateQuery();

      if (!part) {
        return;
      }

      if (section.length > 1) {
        section += ' & ';
      }

      section += part;
    });

    section += close;

    if (section.length > 2) {
      query += section;
    }
  });

  query += ')';

  return query.length > 2 ? query : '';
}

/**
 * Validates and normalizes a full-text search query before sending to the API.
 *
 * Returns null when the query is too short to be useful (threshold differs for
 * trainee users who can only search by phone).
 * Strips non-digit characters from phone-like inputs.
 */
export function normalizeFullTextQuery({
  raw,
  isTrainee,
}: {
  raw: string;
  isTrainee: boolean;
}): string | null {
  const minLength = isTrainee ? 9 : 4;

  if (regexPhone.test(raw)) {
    // but need to clean up special characters out of phone number
    raw = raw.replace(/\D+/g, '');
  }

  if (raw.length < minLength) {
    return null;
  }

  return raw;
}

function isFieldControl(x: unknown): x is FieldControl {
  return typeof (x as FieldControl)?.generateQuery === 'function';
}

interface FieldControl {
  generateQuery(): string;
}

type MainSection = Record<string, unknown>;
